/**
 * Product Category API Helper
 * Manages product categories and their options
 */

import { fetchPaginated } from "./api_helper"
import config from "../config/app.config"

// Base API URLs for product categories (using configured app)
const FLEXFLOW = config.APP_URL
const PRODUCT_CATEGORIES_URL = FLEXFLOW + "_table/product_categories"
const CATEGORY_OPTIONS_URL = FLEXFLOW + "_table/product_category_options"
const CATEGORY_MAPPING_URL = FLEXFLOW + "_table/product_categories_mapping"

// Get auth headers with session token
const getHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "",
      "X-Api-Key": config.APP_API_KEY,
    }
  }
}

/**
 * Fetch all product categories
 */
export const fetchProductCategories = async (limit = 100, offset = 0) => {
  try {
    const response = await fetch(
      `${PRODUCT_CATEGORIES_URL}?limit=${limit}&offset=${offset}&order=name ASC`,
      getHeaders()
    )
    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("Error fetching product categories:", error)
    return []
  }
}

/**
 * Create a new product category
 */
export const createProductCategory = async (categoryData) => {
  try {
    const response = await fetch(PRODUCT_CATEGORIES_URL, {
      method: "POST",
      ...getHeaders(),
      body: JSON.stringify(categoryData)
    })
    const data = await response.json()
    return data.resource || data
  } catch (error) {
    console.error("Error creating product category:", error)
    throw error
  }
}

/**
 * Update a product category
 */
export const updateProductCategory = async (categoryId, categoryData) => {
  try {
    const response = await fetch(`${PRODUCT_CATEGORIES_URL}/${categoryId}`, {
      method: "PATCH",
      ...getHeaders(),
      body: JSON.stringify(categoryData)
    })
    const data = await response.json()
    return data.resource || data
  } catch (error) {
    console.error("Error updating product category:", error)
    throw error
  }
}

/**
 * Delete a product category
 */
export const deleteProductCategory = async (categoryId) => {
  try {
    const response = await fetch(`${PRODUCT_CATEGORIES_URL}/${categoryId}`, {
      method: "DELETE",
      ...getHeaders()
    })
    return response.ok
  } catch (error) {
    console.error("Error deleting product category:", error)
    throw error
  }
}

/**
 * Fetch options for a specific category
 */
export const fetchCategoryOptions = async (categoryId) => {
  try {
    const response = await fetch(
      `${CATEGORY_OPTIONS_URL}?filter=(category_id=${categoryId})&order=option_name ASC`,
      getHeaders()
    )
    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("Error fetching category options:", error)
    return []
  }
}

/**
 * Create a new category option
 */
export const createCategoryOption = async (optionData) => {
  try {
    const response = await fetch(CATEGORY_OPTIONS_URL, {
      method: "POST",
      ...getHeaders(),
      body: JSON.stringify(optionData)
    })
    const data = await response.json()
    return data.resource || data
  } catch (error) {
    console.error("Error creating category option:", error)
    throw error
  }
}

/**
 * Update a category option
 */
export const updateCategoryOption = async (optionId, optionData) => {
  try {
    const response = await fetch(`${CATEGORY_OPTIONS_URL}/${optionId}`, {
      method: "PATCH",
      ...getHeaders(),
      body: JSON.stringify(optionData)
    })
    const data = await response.json()
    return data.resource || data
  } catch (error) {
    console.error("Error updating category option:", error)
    throw error
  }
}

/**
 * Delete a category option
 */
export const deleteCategoryOption = async (optionId) => {
  try {
    const response = await fetch(`${CATEGORY_OPTIONS_URL}/${optionId}`, {
      method: "DELETE",
      ...getHeaders()
    })
    return response.ok
  } catch (error) {
    console.error("Error deleting category option:", error)
    throw error
  }
}

/**
 * Get categories for a product (single API call — no N+1)
 */
export const getProductCategories = async (productId) => {
  try {
    const response = await fetch(
      `${CATEGORY_MAPPING_URL}?filter=(product_id=${productId})`,
      getHeaders()
    )
    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("Error fetching product categories:", error)
    return []
  }
}

/**
 * Assign a category option to a product
 */
export const assignProductCategory = async (productId, optionId) => {
  try {
    const response = await fetch(CATEGORY_MAPPING_URL, {
      method: "POST",
      ...getHeaders(),
      body: JSON.stringify({
        product_id: productId,
        category_option_id: optionId
      })
    })
    const data = await response.json()
    return data.resource || data
  } catch (error) {
    console.error("Error assigning product category:", error)
    throw error
  }
}

/**
 * Remove a category assignment from a product
 */
export const removeProductCategory = async (mappingId) => {
  try {
    const response = await fetch(`${CATEGORY_MAPPING_URL}/${mappingId}`, {
      method: "DELETE",
      ...getHeaders()
    })
    return response.ok
  } catch (error) {
    console.error("Error removing product category:", error)
    throw error
  }
}

/**
 * Save multiple category options for a product (replaces all existing mappings)
 * @param {number} productId - Product ID
 * @param {number[]} optionIds - Array of category_option_id values
 * @returns {boolean} Success status
 */
export const saveProductCategories = async (productId, optionIds = []) => {
  try {
    // Fetch existing mappings for this product
    const existingResponse = await fetch(
      `${CATEGORY_MAPPING_URL}?filter=(product_id=${productId})`,
      getHeaders()
    )
    const existingData = await existingResponse.json()
    const existingMappings = existingData.resource || []

    // Delete mappings that are no longer in optionIds
    const existingOptionIds = new Set(existingMappings.map(m => m.category_option_id))
    const newOptionIds = new Set(optionIds)

    for (const mapping of existingMappings) {
      if (!newOptionIds.has(mapping.category_option_id)) {
        await removeProductCategory(mapping.id)
      }
    }

    // Add new mappings that don't exist yet
    for (const optionId of optionIds) {
      if (!existingOptionIds.has(optionId)) {
        await assignProductCategory(productId, optionId)
      }
    }

    // Mirror StoreTab tags into the legacy `products.category` JSON so the CPQ
    // tabs (which still filter on that field for subcategory scoping) and the
    // Products list page can find the product without a separate migration.
    try {
      await syncStoreTabToLegacyCategory(productId, optionIds)
    } catch (syncErr) {
      // Don't fail the whole save if the mirror step errors — the M2M write
      // is the source of truth.
      console.warn("[saveProductCategories] legacy JSON mirror failed:", syncErr)
    }

    return true
  } catch (error) {
    console.error("Error saving product categories:", error)
    throw error
  }
}

// Legacy string that each StoreTab option corresponds to in `products.category`.
// Keep in sync with scripts/migrate-storetab-categories.sql.
const STORETAB_LEGACY_MAP = {
  Accessories: "Accessories",
  Special: "Special",
  Cladding: "ΠΟΡΤΑ ΕΠΕΝΔΥΣΗΣ",
  Pagkos: "Pagkos",
  Box: "KITCHEN",
}

// Sync StoreTab M2M tags <-> legacy `products.category` JSON, both directions.
// Adds the mapped legacy string for each currently-ticked StoreTab option, and
// removes the mapped legacy string for each StoreTab option that is NOT ticked.
// Only touches the strings this map controls — other legacy tags (subcategory
// scoping like "Handles", "Drawer", "GOLA", etc.) are preserved.
async function syncStoreTabToLegacyCategory(productId, currentOptionIds) {
  const cats = await getAllCategoryOptions()
  const storeTab = cats?.StoreTab
  if (!storeTab || !Array.isArray(storeTab.options)) return

  const wantedTags = new Set()
  const unwantedTags = new Set()
  for (const opt of storeTab.options) {
    const legacy = STORETAB_LEGACY_MAP[opt.name]
    if (!legacy) continue
    if (currentOptionIds.includes(opt.id)) wantedTags.add(legacy)
    else unwantedTags.add(legacy)
  }
  if (wantedTags.size === 0 && unwantedTags.size === 0) return

  const prodResp = await fetch(
    `${FLEXFLOW}_table/products/${productId}`,
    getHeaders(),
  )
  const prodData = await prodResp.json()
  const product = prodData?.resource?.[0] || prodData
  if (!product) return

  let existing = []
  if (product.category) {
    try {
      const parsed = JSON.parse(product.category)
      existing = Array.isArray(parsed) ? parsed : [product.category]
    } catch { existing = [product.category] }
  }

  // Drop any legacy string that maps to an unticked StoreTab option, then
  // add anything ticked that's missing. Case-sensitive on purpose so we only
  // touch our own strings.
  const cleaned = existing.filter(tag => !unwantedTags.has(tag))
  const next = Array.from(new Set([...cleaned, ...wantedTags]))

  // Skip the network call if nothing changed
  if (next.length === existing.length && next.every((v, i) => v === existing[i])) return

  await fetch(`${FLEXFLOW}_table/products/${productId}`, {
    method: "PATCH",
    ...getHeaders(),
    body: JSON.stringify({ category: JSON.stringify(next) }),
  })
}

/**
 * Get all category groups with their options
 * Returns structure: { "Position": { id, options: [{ id, name }] }, ... }
 * Optimized: 2 parallel API calls instead of N+1
 */
export const getAllCategoryOptions = async () => {
  try {
    const [catResponse, optResponse] = await Promise.all([
      fetch(`${PRODUCT_CATEGORIES_URL}?limit=100&offset=0&order=name ASC`, getHeaders()),
      fetch(`${CATEGORY_OPTIONS_URL}?limit=500&offset=0&order=option_name ASC`, getHeaders()),
    ])
    const catData = await catResponse.json()
    const optData = await optResponse.json()
    const categories = catData.resource || []
    const allOptions = optData.resource || []

    // Group options by category_id
    const optionsByCategory = {}
    for (const opt of allOptions) {
      if (!optionsByCategory[opt.category_id]) optionsByCategory[opt.category_id] = []
      optionsByCategory[opt.category_id].push({ id: opt.id, name: opt.option_name, value: opt.option_value || opt.option_name, description: opt.description || "" })
    }

    const result = {}
    for (const category of categories) {
      result[category.name] = {
        id: category.id,
        description: category.description,
        options: optionsByCategory[category.id] || []
      }
    }

    return result
  } catch (error) {
    console.error("Error fetching all category options:", error)
    return {}
  }
}

/**
 * Get category options grouped by category for a specific product
 * Returns: { "Position": ["ΒΑΣΕΩΣ"], "Pomolo": ["GOLA"], ... }
 * Optimized: 3 parallel API calls total (categories + all options + product mappings)
 */
export const getProductCategoriesGrouped = async (productId) => {
  try {
    const [allCategories, productMappings] = await Promise.all([
      getAllCategoryOptions(),
      getProductCategories(productId),
    ])

    const result = {}

    // Build a map of optionId -> { categoryName, optionName }
    const optionMap = {}
    for (const [categoryName, categoryData] of Object.entries(allCategories)) {
      for (const option of categoryData.options) {
        optionMap[option.id] = { categoryName, optionName: option.name }
      }
    }

    // Group product mappings by category
    for (const mapping of productMappings) {
      const optionInfo = optionMap[mapping.category_option_id]
      if (optionInfo) {
        if (!result[optionInfo.categoryName]) {
          result[optionInfo.categoryName] = []
        }
        result[optionInfo.categoryName].push(optionInfo.optionName)
      }
    }

    return result
  } catch (error) {
    console.error("Error getting grouped product categories:", error)
    return {}
  }
}
